// ============================================================
// navGraph.ts — Navigation Graph + A* Pathfinding
// ============================================================
//
// Builds a graph of navigable waypoints from room doorway data.
// Nodes = doorway midpoints (world coords) + room centers.
// Edges = connections between doorways within the same room,
//         and connections between paired doorways of adjacent rooms.
//
// Provides findPath(from, to) → waypoint[] for enemies and bot.
// ============================================================

import type { PlacedRoom, RoomMap } from './roomMapgen';
import { ROOM_SIZE } from './roomMapgen';

// ─── Types ──────────────────────────────────────────────────────────

export interface NavNode {
  id: number;
  x: number;
  y: number;
  /** Which room this node belongs to (index in rooms array), or -1 for open space */
  roomIndex: number;
  /** 'doorway' or 'center' */
  type: 'doorway' | 'center';
  /** Direction of doorway (for doorway nodes) */
  dir?: 'north' | 'south' | 'east' | 'west';
}

export interface NavEdge {
  from: number; // node id
  to: number;   // node id
  cost: number; // distance
}

export interface NavGraph {
  nodes: NavNode[];
  edges: NavEdge[];
  /** Adjacency list: nodeId → [{neighborId, cost}] */
  adj: Map<number, Array<{ to: number; cost: number }>>;
}

// ─── Graph Building ─────────────────────────────────────────────────

let cachedGraph: NavGraph | null = null;
let cachedSeed: number = -1;

/**
 * Build (or return cached) navigation graph from room map data.
 * Call once per map generation — cached until seed changes.
 */
export function getNavGraph(roomMap: RoomMap): NavGraph {
  if (cachedGraph && cachedSeed === roomMap.seed) return cachedGraph;

  const nodes: NavNode[] = [];
  const edges: NavEdge[] = [];
  let nextId = 0;

  // Build grid lookup for neighbor detection
  const roomGrid = new Map<string, number>(); // "gx,gy" → room index
  for (let i = 0; i < roomMap.rooms.length; i++) {
    const room = roomMap.rooms[i];
    const gx = Math.round(room.x / ROOM_SIZE);
    const gy = Math.round(room.y / ROOM_SIZE);
    roomGrid.set(`${gx},${gy}`, i);
  }

  // For each room: create center node + doorway nodes
  const roomCenterNodes: number[] = []; // roomIndex → center node id
  // doorwayNodes[roomIndex][dir] = node id
  const doorwayNodes: Map<number, Map<string, number>> = new Map();

  for (let ri = 0; ri < roomMap.rooms.length; ri++) {
    const room = roomMap.rooms[ri];
    const gx = Math.round(room.x / ROOM_SIZE);
    const gy = Math.round(room.y / ROOM_SIZE);

    // Center node
    const centerId = nextId++;
    nodes.push({
      id: centerId,
      x: room.x + ROOM_SIZE / 2,
      y: room.y + ROOM_SIZE / 2,
      roomIndex: ri,
      type: 'center',
    });
    roomCenterNodes.push(centerId);

    // Doorway nodes — only create if there's a neighbor in that direction
    // (i.e., the doorway is not barricaded)
    const dirMap = new Map<string, number>();
    doorwayNodes.set(ri, dirMap);

    const hasN = roomGrid.has(`${gx},${gy - 1}`);
    const hasS = roomGrid.has(`${gx},${gy + 1}`);
    const hasE = roomGrid.has(`${gx + 1},${gy}`);
    const hasW = roomGrid.has(`${gx - 1},${gy}`);

    // Check barricades — if a direction is barricaded, skip it
    const barricadedDirs = new Set(room.barricades.map(b => b.dir));

    if (hasN && !barricadedDirs.has('north')) {
      const nid = nextId++;
      // Doorway midpoint: center of the north gap
      nodes.push({
        id: nid,
        x: room.x + ROOM_SIZE / 2,
        y: room.y, // at the room's north edge
        roomIndex: ri,
        type: 'doorway',
        dir: 'north',
      });
      dirMap.set('north', nid);
    }
    if (hasS && !barricadedDirs.has('south')) {
      const nid = nextId++;
      nodes.push({
        id: nid,
        x: room.x + ROOM_SIZE / 2,
        y: room.y + ROOM_SIZE, // at the room's south edge
        roomIndex: ri,
        type: 'doorway',
        dir: 'south',
      });
      dirMap.set('south', nid);
    }
    if (hasE && !barricadedDirs.has('east')) {
      const nid = nextId++;
      nodes.push({
        id: nid,
        x: room.x + ROOM_SIZE, // at the room's east edge
        y: room.y + ROOM_SIZE / 2,
        roomIndex: ri,
        type: 'doorway',
        dir: 'east',
      });
      dirMap.set('east', nid);
    }
    if (hasW && !barricadedDirs.has('west')) {
      const nid = nextId++;
      nodes.push({
        id: nid,
        x: room.x, // at the room's west edge
        y: room.y + ROOM_SIZE / 2,
        roomIndex: ri,
        type: 'doorway',
        dir: 'west',
      });
      dirMap.set('west', nid);
    }

    // Edges: center ↔ each doorway in this room
    dirMap.forEach((doorId) => {
      const dx = nodes[doorId].x - nodes[centerId].x;
      const dy = nodes[doorId].y - nodes[centerId].y;
      const cost = Math.sqrt(dx * dx + dy * dy);
      edges.push({ from: centerId, to: doorId, cost });
      edges.push({ from: doorId, to: centerId, cost });
    });

    // Edges: doorway ↔ doorway within same room (for cutting across)
    const doorIds = Array.from(dirMap.values());
    for (let i = 0; i < doorIds.length; i++) {
      for (let j = i + 1; j < doorIds.length; j++) {
        const a = doorIds[i], b = doorIds[j];
        const dx = nodes[a].x - nodes[b].x;
        const dy = nodes[a].y - nodes[b].y;
        const cost = Math.sqrt(dx * dx + dy * dy);
        edges.push({ from: a, to: b, cost });
        edges.push({ from: b, to: a, cost });
      }
    }
  }

  // Edges: connect paired doorways between adjacent rooms
  // e.g., room A's south doorway connects to room B's north doorway (room B is south of A)
  for (let ri = 0; ri < roomMap.rooms.length; ri++) {
    const room = roomMap.rooms[ri];
    const gx = Math.round(room.x / ROOM_SIZE);
    const gy = Math.round(room.y / ROOM_SIZE);
    const myDoors = doorwayNodes.get(ri)!;

    // South → neighbor's North
    const southNeighborIdx = roomGrid.get(`${gx},${gy + 1}`);
    if (southNeighborIdx !== undefined) {
      const myS = myDoors.get('south');
      const theirN = doorwayNodes.get(southNeighborIdx)?.get('north');
      if (myS !== undefined && theirN !== undefined) {
        // These doorways are essentially the same point (or very close)
        const cost = 10; // small cost for crossing the threshold
        edges.push({ from: myS, to: theirN, cost });
        edges.push({ from: theirN, to: myS, cost });
      }
    }

    // East → neighbor's West
    const eastNeighborIdx = roomGrid.get(`${gx + 1},${gy}`);
    if (eastNeighborIdx !== undefined) {
      const myE = myDoors.get('east');
      const theirW = doorwayNodes.get(eastNeighborIdx)?.get('west');
      if (myE !== undefined && theirW !== undefined) {
        const cost = 10;
        edges.push({ from: myE, to: theirW, cost });
        edges.push({ from: theirW, to: myE, cost });
      }
    }
  }

  // Build adjacency list
  const adj = new Map<number, Array<{ to: number; cost: number }>>();
  for (const node of nodes) {
    adj.set(node.id, []);
  }
  for (const edge of edges) {
    adj.get(edge.from)!.push({ to: edge.to, cost: edge.cost });
  }

  cachedGraph = { nodes, edges, adj };
  cachedSeed = roomMap.seed;
  return cachedGraph;
}

// ─── A* Pathfinding ─────────────────────────────────────────────────

/**
 * Find the nearest nav node to a world position.
 * Returns the node id, or -1 if no nodes exist.
 */
export function findNearestNode(graph: NavGraph, wx: number, wy: number): number {
  let bestId = -1;
  let bestDist = Infinity;
  for (const node of graph.nodes) {
    const dx = node.x - wx;
    const dy = node.y - wy;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      bestId = node.id;
    }
  }
  return bestId;
}

/**
 * A* pathfinding from startNode to goalNode.
 * Returns array of world-coordinate waypoints (excluding start), or empty if no path.
 */
export function astarPath(
  graph: NavGraph,
  startId: number,
  goalId: number,
): Array<{ x: number; y: number }> {
  if (startId === goalId) return [];
  if (startId < 0 || goalId < 0) return [];

  const goalNode = graph.nodes[goalId];
  const heuristic = (nodeId: number) => {
    const n = graph.nodes[nodeId];
    return Math.abs(n.x - goalNode.x) + Math.abs(n.y - goalNode.y);
  };

  // Open set as a simple priority queue (sorted array — fine for small graphs)
  const openSet: number[] = [startId];
  const cameFrom = new Map<number, number>();
  const gScore = new Map<number, number>();
  const fScore = new Map<number, number>();

  gScore.set(startId, 0);
  fScore.set(startId, heuristic(startId));

  while (openSet.length > 0) {
    // Find node with lowest fScore
    let bestIdx = 0;
    let bestF = fScore.get(openSet[0]) ?? Infinity;
    for (let i = 1; i < openSet.length; i++) {
      const f = fScore.get(openSet[i]) ?? Infinity;
      if (f < bestF) { bestF = f; bestIdx = i; }
    }
    const current = openSet[bestIdx];

    if (current === goalId) {
      // Reconstruct path
      const path: Array<{ x: number; y: number }> = [];
      let node = goalId;
      while (node !== startId) {
        path.push({ x: graph.nodes[node].x, y: graph.nodes[node].y });
        node = cameFrom.get(node)!;
      }
      path.reverse();
      return path;
    }

    openSet.splice(bestIdx, 1);
    const currentG = gScore.get(current) ?? Infinity;

    const neighbors = graph.adj.get(current);
    if (!neighbors) continue;

    for (const { to, cost } of neighbors) {
      const tentativeG = currentG + cost;
      if (tentativeG < (gScore.get(to) ?? Infinity)) {
        cameFrom.set(to, current);
        gScore.set(to, tentativeG);
        fScore.set(to, tentativeG + heuristic(to));
        if (!openSet.includes(to)) {
          openSet.push(to);
        }
      }
    }
  }

  return []; // No path found
}

// ─── High-Level API ─────────────────────────────────────────────────

/**
 * Find a path from world position (fx, fy) to world position (tx, ty).
 * Returns an array of waypoints to follow, or empty if direct line is fine.
 *
 * The caller should:
 *   1. Check if they can beeline to target (no wall between them).
 *   2. If not, call findPath() to get waypoints.
 *   3. Move toward waypoints[0], pop it when close enough, repeat.
 */
export function findPath(
  roomMap: RoomMap,
  fromX: number, fromY: number,
  toX: number, toY: number,
): Array<{ x: number; y: number }> {
  const graph = getNavGraph(roomMap);
  if (graph.nodes.length === 0) return [];

  const startId = findNearestNode(graph, fromX, fromY);
  const goalId = findNearestNode(graph, toX, toY);

  if (startId < 0 || goalId < 0) return [];
  if (startId === goalId) return [{ x: toX, y: toY }];

  return astarPath(graph, startId, goalId);
}

/**
 * Lightweight check: can an entity at (fx,fy) see/reach (tx,ty) in a straight line
 * without crossing any exterior green polygon?
 * Used to decide whether to beeline or pathfind.
 */
export function hasLineOfSight(
  roomMap: RoomMap,
  fx: number, fy: number,
  tx: number, ty: number,
  isWalkableCheck: (x: number, y: number) => boolean,
  steps = 8,
): boolean {
  const dx = tx - fx;
  const dy = ty - fy;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    if (!isWalkableCheck(fx + dx * t, fy + dy * t)) return false;
  }
  return true;
}
